import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  Alert,
  CircularProgress,
  Avatar,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  IconButton,
  Paper,
  Divider,
  useTheme,
  alpha,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Slide,
  styled,
  keyframes,
  Tooltip,
  Collapse,
  Fab,
} from '@mui/material';
import {
  Search,
  Warning,
  TrendingUp,
  TrendingDown,
  Analytics as AnalyticsIcon,
  Speed,
  Assignment,
  Science,
  People,
  Schedule,
  CheckCircle,
  Tv as TvIcon,
  Error,
  Refresh,
  FilterList,
  Download,
  Visibility,
  NotificationsActive,
  Dashboard,
  BarChart,
  PieChart,
  Timeline,
  Close,
  Security,
  // Network diagram icons
  Inventory2,
  PrecisionManufacturing,
  Psychology,
  Whatshot,
  LocalPharmacy,
  Build,
  InvertColors,
  AutoGraph,
  Diamond,
  WaterDrop,
  GppGood,
  MenuBook,
  TaskAlt,
  // Batch visualization icons - MISSING IMPORTS
  ExpandLess,
  ExpandMore,
  QueryStats,
  AccountTree,
  Schema,
  MonitorHeart,
  NetworkCheck,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import samplesService, { SampleStatistics, Sample, SampleStatus } from '../services/samplesService';
import api from '../services/api';
import { useOptimizedStatistics } from '../hooks/useOptimizedStatistics';

// Network diagram animations
const networkPulse = keyframes`
  0% { 
    transform: scale(1) rotate(0deg); 
    box-shadow: 0 0 20px rgba(37, 99, 235, 0.3);
  }
  50% { 
    transform: scale(1.1) rotate(2deg); 
    box-shadow: 0 0 40px rgba(37, 99, 235, 0.6);
  }
  100% { 
    transform: scale(1) rotate(0deg); 
    box-shadow: 0 0 20px rgba(37, 99, 235, 0.3);
  }
`;

// Styled components for the network visualization
const SankeyContainer = styled(Box)(({ theme }) => ({
  position: 'relative',
  width: '100%',
  height: '500px',
  background: `linear-gradient(135deg, 
    ${alpha(theme.palette.background.paper, 0.95)} 0%, 
    ${alpha(theme.palette.primary.main, 0.02)} 50%, 
    ${alpha(theme.palette.secondary.main, 0.02)} 100%)`,
  borderRadius: theme.spacing(3),
  overflow: 'hidden',
  border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
  backdropFilter: 'blur(20px)',
}));

const InteractiveCard = styled(Box)<{ 
  active?: boolean; 
  nodeType?: 'reception' | 'preparation' | 'laboratory' | 'validation' | 'certificate' | 'complete';
  laboratory?: string;
  sampleCount?: number;
}>(({ theme, active, nodeType, laboratory, sampleCount = 0 }) => {
  const getNodeColor = () => {
    switch (nodeType) {
      case 'reception': return { main: '#3b82f6', dark: '#1d4ed8', accent: '#60a5fa' };
      case 'preparation': return { main: '#7c3aed', dark: '#5b21b6', accent: '#a78bfa' };
      case 'laboratory': 
        switch (laboratory) {
          case 'SAA': return { main: '#dc2626', dark: '#991b1b', accent: '#f87171' };
          case 'ICP': return { main: '#059669', dark: '#047857', accent: '#34d399' };
          case 'AU': return { main: '#d97706', dark: '#92400e', accent: '#fbbf24' };
          case 'VOLUMETRIE': return { main: '#7c2d12', dark: '#451a03', accent: '#a16207' };
          default: return { main: '#6b7280', dark: '#374151', accent: '#9ca3af' };
        }
      case 'validation': return { main: '#ea580c', dark: '#c2410c', accent: '#fb923c' };
      case 'certificate': return { main: '#0891b2', dark: '#0e7490', accent: '#22d3ee' };
      case 'complete': return { main: '#16a34a', dark: '#15803d', accent: '#4ade80' };
      default: return { main: '#6b7280', dark: '#374151', accent: '#9ca3af' };
    }
  };

  const colors = getNodeColor();
  const intensity = Math.min(sampleCount / 10, 1);
  
  return {
    position: 'absolute',
    cursor: 'pointer',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    width: '120px',
    height: '100px',
    borderRadius: '20px',
    background: `
      radial-gradient(circle at 30% 30%, ${alpha(colors.accent, 0.3)}, transparent 70%),
      linear-gradient(135deg, ${colors.main} 0%, ${colors.dark} 50%, ${colors.main} 100%)
    `,
    border: active 
      ? `2px solid ${theme.palette.warning.main}` 
      : `1px solid ${alpha(colors.accent, 0.4)}`,
    color: theme.palette.common.white,
    transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
    transform: active ? 'scale(1.15) rotateY(5deg)' : 'scale(1)',
    zIndex: active ? 20 : sampleCount > 0 ? 10 : 5,
    boxShadow: active 
      ? `0 0 30px ${alpha(colors.main, 0.6)}, 0 10px 40px ${alpha(colors.main, 0.3)}, inset 0 1px 0 ${alpha(theme.palette.common.white, 0.2)}`
      : `0 4px 20px ${alpha(colors.main, intensity * 0.4)}, 0 2px 10px ${alpha(colors.main, 0.2)}, inset 0 1px 0 ${alpha(theme.palette.common.white, 0.1)}`,
    backdropFilter: 'blur(20px)',
    overflow: 'hidden',
    '&:hover': {
      transform: 'scale(1.2) translateY(-8px) rotateY(10deg)',
      boxShadow: `0 0 40px ${alpha(colors.main, 0.8)}, 0 20px 60px ${alpha(colors.main, 0.4)}, inset 0 1px 0 ${alpha(theme.palette.common.white, 0.3)}`,
      zIndex: 25,
      '& .pulse-ring': {
        animation: 'pulse-ring 1.5s cubic-bezier(0.215, 0.61, 0.355, 1) infinite',
      },
      '& .icon-container': {
        transform: 'scale(1.3) rotate(5deg)',
      },
    },
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      borderRadius: '20px',
      background: `conic-gradient(from 0deg, ${alpha(colors.accent, 0.1)}, ${alpha(colors.main, 0.2)}, ${alpha(colors.accent, 0.1)})`,
      opacity: intensity,
      animation: active ? 'spin 3s linear infinite' : 'none',
    },
    '&::after': {
      content: '""',
      position: 'absolute',
      top: '2px',
      left: '2px',
      right: '2px',
      bottom: '2px',
      borderRadius: '18px',
      background: `linear-gradient(135deg, ${alpha(theme.palette.common.white, 0.1)} 0%, transparent 50%, ${alpha(colors.accent, 0.1)} 100%)`,
      pointerEvents: 'none',
    },
    '@keyframes pulse-ring': {
      '0%': { transform: 'scale(1)', opacity: 1 },
      '100%': { transform: 'scale(1.5)', opacity: 0 },
    },
    '@keyframes spin': {
      '0%': { transform: 'rotate(0deg)' },
      '100%': { transform: 'rotate(360deg)' },
    },
  };
});

const SankeyFlow = styled('svg')<{ active?: boolean; thickness?: number }>(({ theme, active, thickness = 1 }) => ({
  position: 'absolute',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
  pointerEvents: 'none',
  zIndex: 2,
  '& .flow-path': {
    fill: 'none',
    stroke: active ? theme.palette.warning.main : alpha(theme.palette.primary.main, 0.4),
    strokeWidth: Math.max(2, thickness * 20),
    opacity: active ? 0.8 : 0.6,
    transition: 'all 0.4s ease',
    filter: active ? `drop-shadow(0 0 8px ${theme.palette.warning.main})` : 'none',
  },
}));

interface NetworkNodeData {
  id: string;
  name: string;
  shortName: string;
  icon: React.ReactNode;
  x: number;
  y: number;
  type: 'reception' | 'preparation' | 'laboratory' | 'validation' | 'certificate' | 'complete';
  laboratory?: string;
  stage?: string;
  count: number;
  connections: string[];
  description: string;
}

interface AdminMetrics {
  total_samples: number;
  samples_completed_today: number;
  active_laboratories: number;
  total_users: number;
  system_efficiency: number;
  alerts_count: number;
}

interface QuickAction {
  title: string;
  description: string;
  icon: React.ReactNode;
  action: () => void;
  color: string;
}

// Define proper TypeScript interfaces
interface DashboardStatistics {
  total: number;
  par_statut: Record<string, number>;
  par_type: Record<string, number>;
  par_priorite: Record<string, number>;
  en_retard: number;
  termines_aujourd_hui: number;
  moyenne_duree_jours: number;
}

interface ValidationData {
  SAA: number;
  ICP: number;
  AU: number;
  VOLUMETRIE: number;
  TOTAL: number;
}

const AdminDashboard: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const theme = useTheme();
  const sankeyRef = useRef<HTMLDivElement>(null);

  // 🚀 REAL-TIME DASHBOARD: Use optimized statistics hook with caching and throttling
  // No date filtering - this shows current laboratory status in real-time
  const { 
    statistics, 
    derivedMetrics, 
    loading: statsLoading, 
    error: statsError, 
    refresh: refreshStatistics 
  } = useOptimizedStatistics(30000); // Refresh every 30 seconds for real-time data
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [validationData, setValidationData] = useState<ValidationData | null>(null);
  const [metrics, setMetrics] = useState<AdminMetrics | null>(null);
  const [actualValidationCounts, setActualValidationCounts] = useState<{[key: string]: number}>({});
  
  // Network diagram state
  const [selectedNode, setSelectedNode] = useState<NetworkNodeData | null>(null);
  const [stageSamples, setStageSamples] = useState<Sample[]>([]);
  const [stageSamplesTotal, setStageSamplesTotal] = useState(0); // Track real total count
  const [dialogOpen, setDialogOpen] = useState(false);
  const [loadingSamples, setLoadingSamples] = useState(false);
  const [activeConnections, setActiveConnections] = useState<Set<string>>(new Set());
  
  // 🚀 PERFORMANCE FIX: Use optimized workflow counts from backend
  // Instead of fetching 60k+ samples and filtering in JS, we now use a single optimized query
  const [workflowCounts, setWorkflowCounts] = useState<any>(null);
  
  // 🔥 BGM FIX: Store accurate counts for nodes with BGM filtering (now from workflowCounts)
  const [accurateCounts, setAccurateCounts] = useState<{[key: string]: number}>({
    prep_mecanique: statistics?.par_statut?.PREPARATION_MECANIQUE || 0,
    icp_prep: statistics?.par_statut?.LABORATOIRE_ICP_PREP_CHIMIQUE || 0,
    au_prep: statistics?.par_statut?.LABORATOIRE_AU_PREP_CHIMIQUE || 0,
    icp_analyse: statistics?.par_statut?.LABORATOIRE_ICP_ANALYSE || 0,
    au_analyse: statistics?.par_statut?.LABORATOIRE_AU_ANALYSE || 0,
  });

  // Batch visualization state
  const [expandedBatches, setExpandedBatches] = useState<Set<string>>(new Set());
  const [selectedBatchForView, setSelectedBatchForView] = useState<{
    batchNumber: string;
    samples: any[];
    laboratory: string;
  } | null>(null);
  const [batchViewDialog, setBatchViewDialog] = useState(false);

  // Modal pagination state - PERFORMANCE OPTIMIZATION
  const [modalPage, setModalPage] = useState(1);
  const [modalPageSize] = useState(150); // Load 150 samples at a time for better batch visualization (5-7 batches)
  const [modalTotalPages, setModalTotalPages] = useState(1);
  

  // Helper functions - MUST BE DEFINED BEFORE networkNodes array
  const getTotalSamples = () => {
    return Object.values(statistics?.par_statut || {}).reduce((sum, count) => sum + count, 0);
  };

  // Calculate duration since reception (same logic as client dashboard)
  const calculateDuration = (receptionDate: string): string => {
    const reception = new Date(receptionDate);
    const now = new Date();
    const diffMs = now.getTime() - reception.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    const diffHours = Math.floor((diffMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    
    if (diffDays > 0) {
      return `${diffDays}j ${diffHours}h`;
    } else {
      return `${diffHours}h`;
    }
  };

  // 🚀 PERFORMANCE OPTIMIZATION: Load all workflow counts in a SINGLE optimized backend query
  // This replaces the old approach of fetching 60,000+ samples and filtering in JavaScript
  // Performance improvement: From ~5-10 seconds to ~100ms
  const loadWorkflowCounts = async () => {
    try {
      console.time('⏱️ Workflow counts load time');
      
      // Single optimized API call - all filtering done in database
      const counts = await api.get<any>('/samples/workflow-counts');
      
      console.timeEnd('⏱️ Workflow counts load time');
      console.log('🚀 Workflow counts loaded (optimized):', counts);
      
      // Store the full workflow counts
      setWorkflowCounts(counts);
      
      // Update accurate counts for backward compatibility with existing node definitions
      setAccurateCounts({
        prep_mecanique: counts.preparation_mecanique || 0,
        icp_prep: counts.icp_prep || 0,
        au_prep: counts.au_prep || 0,
        icp_analyse: counts.icp_analysis || 0,
        au_analyse: counts.au_analysis || 0,
      });
      
      // Update validation counts
      setActualValidationCounts({
        SAA: counts.validation_saa || 0,
        ICP: counts.validation_icp || 0,
        AU: counts.validation_au || 0,
        VOLUMETRIE: counts.validation_volumetrie || 0,
      });
      
    } catch (error) {
      console.error('Error loading workflow counts:', error);
      console.timeEnd('⏱️ Workflow counts load time');
      
      // Fallback to statistics-based counts
      if (statistics) {
      setAccurateCounts({
          prep_mecanique: statistics.par_statut?.PREPARATION_MECANIQUE || 0,
          icp_prep: statistics.par_statut?.LABORATOIRE_ICP_PREP_CHIMIQUE || 0,
          au_prep: statistics.par_statut?.LABORATOIRE_AU_PREP_CHIMIQUE || 0,
          icp_analyse: statistics.par_statut?.LABORATOIRE_ICP_ANALYSE || 0,
          au_analyse: statistics.par_statut?.LABORATOIRE_AU_ANALYSE || 0,
        });
      }
    }
  };

  // Helper function to get laboratory-specific validation counts
  // 🚀 OPTIMIZED: Uses counts from loadWorkflowCounts (single backend query)
  const getValidationSamplesForLab = (laboratory: string): number => {
    // 🎯 PRIMARY: Use actual validation counts if available
    if (Object.keys(actualValidationCounts).length > 0) {
      return actualValidationCounts[laboratory] || 0;
    }
    
    // 🎯 SECONDARY: Use validation_source_laboratory from backend if available
    if (validationData && Object.values(validationData).some(count => count > 0)) {
      return validationData[laboratory] || 0;
    }
    
    // 🔧 FALLBACK: Use proportional distribution (temporary until counts load)
    const totalValidation = statistics?.par_statut?.VALIDATION_SERVICE || 0;
    if (totalValidation === 0) return 0;
    
    const analysisCompletedCounts = {
      'SAA': statistics?.par_statut?.LABORATOIRE_SAA_ANALYSE || 0,
      'ICP': statistics?.par_statut?.LABORATOIRE_ICP_ANALYSE || 0,
      'AU': statistics?.par_statut?.LABORATOIRE_AU_ANALYSE || 0,
      'VOLUMETRIE': statistics?.par_statut?.LABORATOIRE_VOLUMETRIE_ANALYSE || 0,
    };
    
    const totalAnalysisCompleted = Object.values(analysisCompletedCounts).reduce((sum, count) => sum + count, 0);
    if (totalAnalysisCompleted === 0) return Math.round(totalValidation / 4);
    
    const labAnalysisCount = analysisCompletedCounts[laboratory] || 0;
    return Math.round((labAnalysisCount / totalAnalysisCompleted) * totalValidation);
  };

  // Define network nodes with REAL DATA from statistics
  
  const networkNodes: NetworkNodeData[] = [
    // Column 1: Entry Point (X = 50)
    {
      id: 'reception',
      name: 'Réception',
      shortName: 'Réception\nÉchantillons',
      icon: <Inventory2 sx={{ fontSize: 16, color: '#fff' }} />,
      x: 50,
      y: 200,
      type: 'reception',
      count: statistics?.par_statut?.RECEPTION || 0,
      connections: ['prep_mecanique'],
      description: 'Accueil et enregistrement des échantillons',
    },

    // Column 2: Mechanical Prep (X = 220)
    {
      id: 'prep_mecanique',
      name: 'Préparation Mécanique',
      shortName: 'Préparation\nMécanique',
      icon: <PrecisionManufacturing sx={{ fontSize: 16, color: '#fff' }} />,
      x: 220,
      y: 200,
      type: 'preparation',
      count: accurateCounts.prep_mecanique || statistics?.par_statut?.PREPARATION_MECANIQUE || 0,
      connections: ['saa_prep', 'icp_prep', 'au_prep', 'vol_prep'],
      description: 'Broyage, tamisage et homogénéisation',
    },

    // Column 3: Chemical Preparation (X = 390)
    {
      id: 'saa_prep',
      name: 'SAA Préparation',
      shortName: 'Préparation\nSAA',
      icon: <Psychology sx={{ fontSize: 16, color: '#fff' }} />,
      x: 390,
      y: 40,
      type: 'laboratory',
      laboratory: 'SAA',
      stage: 'PREP_CHIMIQUE',
      count: statistics?.par_statut?.LABORATOIRE_SAA_PREP_CHIMIQUE || 0,
      connections: ['saa_analyse'],
      description: 'Digestion SAA',
    },
    {
      id: 'icp_prep',
      name: 'ICP Préparation',
      shortName: 'Préparation\nICP',
      icon: <LocalPharmacy sx={{ fontSize: 16, color: '#fff' }} />,
      x: 390,
      y: 150,
      type: 'laboratory',
      laboratory: 'ICP',
      stage: 'PREP_CHIMIQUE',
      count: accurateCounts.icp_prep || statistics?.par_statut?.LABORATOIRE_ICP_PREP_CHIMIQUE || 0,
      connections: ['icp_analyse'],
      description: 'Digestion ICP',
    },
    {
      id: 'au_prep',
      name: 'AU Préparation',
      shortName: 'Préparation\nAU',
      icon: <Build sx={{ fontSize: 16, color: '#fff' }} />,
      x: 390,
      y: 260,
      type: 'laboratory',
      laboratory: 'AU',
      stage: 'PREP_CHIMIQUE',
      count: accurateCounts.au_prep || statistics?.par_statut?.LABORATOIRE_AU_PREP_CHIMIQUE || 0,
      connections: ['au_analyse'],
      description: 'Digestion AU',
    },
    {
      id: 'vol_prep',
      name: 'VOL Préparation',
      shortName: 'Préparation\nVolumétrie',
      icon: <InvertColors sx={{ fontSize: 16, color: '#fff' }} />,
      x: 390,
      y: 370,
      type: 'laboratory',
      laboratory: 'VOLUMETRIE',
      stage: 'PREP_CHIMIQUE',
      count: statistics?.par_statut?.LABORATOIRE_VOLUMETRIE_PREP_CHIMIQUE || 0,
      connections: ['vol_analyse'],
      description: 'Préparation Volumétrie',
    },

    // Column 4: Analysis (X = 560)
    {
      id: 'saa_analyse',
      name: 'SAA Analyse',
      shortName: 'Analyse\nSAA',
      icon: <Whatshot sx={{ fontSize: 16, color: '#fff' }} />,
      x: 560,
      y: 40,
      type: 'laboratory',
      laboratory: 'SAA',
      stage: 'ANALYSE',
      count: statistics?.par_statut?.LABORATOIRE_SAA_ANALYSE || 0,
      connections: ['saa_validation'],
      description: 'Absorption atomique',
    },
    {
      id: 'icp_analyse',
      name: 'ICP Analyse',
      shortName: 'Analyse\nICP',
      icon: <AutoGraph sx={{ fontSize: 16, color: '#fff' }} />,
      x: 560,
      y: 150,
      type: 'laboratory',
      laboratory: 'ICP',
      stage: 'ANALYSE',
      count: accurateCounts.icp_analyse || statistics?.par_statut?.LABORATOIRE_ICP_ANALYSE || 0,
      connections: ['icp_validation'],
      description: 'ICP-MS/OES',
    },
    {
      id: 'au_analyse',
      name: 'AU Analyse',
      shortName: 'Analyse\nAU',
      icon: <Diamond sx={{ fontSize: 16, color: '#fff' }} />,
      x: 560,
      y: 260,
      type: 'laboratory',
      laboratory: 'AU',
      stage: 'ANALYSE',
      count: accurateCounts.au_analyse || statistics?.par_statut?.LABORATOIRE_AU_ANALYSE || 0,
      connections: ['au_validation'],
      description: 'Coupellation',
    },
    {
      id: 'vol_analyse',
      name: 'VOL Analyse',
      shortName: 'Analyse\nVolumétrie',
      icon: <WaterDrop sx={{ fontSize: 16, color: '#fff' }} />,
      x: 560,
      y: 370,
      type: 'laboratory',
      laboratory: 'VOLUMETRIE',
      stage: 'ANALYSE',
      count: statistics?.par_statut?.LABORATOIRE_VOLUMETRIE_ANALYSE || 0,
      connections: ['vol_validation'],
      description: 'Titrage',
    },

    // Column 5: Validation (X = 730)
    {
      id: 'saa_validation',
      name: 'SAA Validation',
      shortName: 'Validation\nSAA',
      icon: <GppGood sx={{ fontSize: 16, color: '#fff' }} />,
      x: 730,
      y: 40,
      type: 'validation',
      laboratory: 'SAA',
      count: getValidationSamplesForLab('SAA'),
      connections: ['certificate'],
      description: 'Validation SAA',
    },
    {
      id: 'icp_validation',
      name: 'ICP Validation',
      shortName: 'Validation\nICP',
      icon: <GppGood sx={{ fontSize: 16, color: '#fff' }} />,
      x: 730,
      y: 150,
      type: 'validation',
      laboratory: 'ICP',
      count: getValidationSamplesForLab('ICP'),
      connections: ['certificate'],
      description: 'Validation ICP',
    },
    {
      id: 'au_validation',
      name: 'AU Validation',
      shortName: 'Validation\nAU',
      icon: <GppGood sx={{ fontSize: 16, color: '#fff' }} />,
      x: 730,
      y: 260,
      type: 'validation',
      laboratory: 'AU',
      count: getValidationSamplesForLab('AU'),
      connections: ['certificate'],
      description: 'Validation AU',
    },
    {
      id: 'vol_validation',
      name: 'VOL Validation',
      shortName: 'Validation\nVolumétrie',
      icon: <GppGood sx={{ fontSize: 16, color: '#fff' }} />,
      x: 730,
      y: 370,
      type: 'validation',
      laboratory: 'VOLUMETRIE',
      count: getValidationSamplesForLab('VOLUMETRIE'),
      connections: ['certificate'],
      description: 'Validation VOL',
    },

    // Column 6: Final Stages (X = 900)
    {
      id: 'certificate',
      name: 'Certificat',
      shortName: 'Génération\nCertificat',
      icon: <MenuBook sx={{ fontSize: 16, color: '#fff' }} />,
      x: 900,
      y: 160,
      type: 'certificate',
      count: statistics?.par_statut?.GENERATION_CERTIFICAT || 0,
      connections: ['complete'],
      description: 'Génération certificats',
    },
    {
      id: 'complete',
      name: 'Terminé',
      shortName: 'Échantillons\nTerminés',
      icon: <TaskAlt sx={{ fontSize: 16, color: '#fff' }} />,
      x: 900,
      y: 270,
      type: 'complete',
      count: statistics?.par_statut?.PRET || 0,
      connections: [],
      description: 'Échantillons prêts',
    },
  ];

  // Batch functions - MISSING FROM PREVIOUS IMPLEMENTATION
  const groupSamplesByBatch = (samples: any[]) => {
    const batches: Record<string, any[]> = {};
    const individualSamples: any[] = [];

    samples.forEach(sample => {
      const batchInfo = sample.metadonnees?.batch_info;
      if (batchInfo && batchInfo.is_batch_sample && batchInfo.batch_number) {
        if (!batches[batchInfo.batch_number]) {
          batches[batchInfo.batch_number] = [];
        }
        batches[batchInfo.batch_number].push(sample);
      } else {
        individualSamples.push(sample);
      }
    });

    return { batches, individualSamples };
  };

  const isBatchSample = (sample: Sample): boolean => {
    return sample.metadonnees?.batch_info?.is_batch_sample === true;
  };

  const getBatchNumber = (sample: Sample): string | null => {
    return sample.metadonnees?.batch_info?.batch_number || null;
  };

  const toggleBatchExpansion = (batchNumber: string) => {
    const newExpanded = new Set(expandedBatches);
    if (newExpanded.has(batchNumber)) {
      newExpanded.delete(batchNumber);
    } else {
      newExpanded.add(batchNumber);
    }
    setExpandedBatches(newExpanded);
  };

  const handleViewBatch = (batchNumber: string, samples: Sample[], laboratory: string) => {
    setSelectedBatchForView({ batchNumber, samples, laboratory });
    setBatchViewDialog(true);
  };

  const getStatusDisplay = (statut: string) => {
    switch (statut) {
      case 'RECEPTION': return { text: 'Réception', color: theme.palette.info.main, bgColor: theme.palette.info.main };
      case 'PREPARATION_MECANIQUE': return { text: 'Préparation Mécanique', color: theme.palette.warning.main, bgColor: theme.palette.warning.main };
      case 'LABORATOIRE_SAA_PREP_CHIMIQUE': return { text: 'Préparation SAA', color: theme.palette.warning.main, bgColor: theme.palette.warning.main };
      case 'LABORATOIRE_SAA_ANALYSE': return { text: 'Analyse SAA', color: theme.palette.primary.main, bgColor: theme.palette.primary.main };
      case 'LABORATOIRE_ICP_PREP_CHIMIQUE': return { text: 'Préparation ICP', color: theme.palette.warning.main, bgColor: theme.palette.warning.main };
      case 'LABORATOIRE_ICP_ANALYSE': return { text: 'Analyse ICP', color: theme.palette.primary.main, bgColor: theme.palette.primary.main };
      case 'LABORATOIRE_AU_PREP_CHIMIQUE': return { text: 'Préparation AU', color: theme.palette.warning.main, bgColor: theme.palette.warning.main };
      case 'LABORATOIRE_AU_ANALYSE': return { text: 'Analyse AU', color: theme.palette.primary.main, bgColor: theme.palette.primary.main };
      case 'LABORATOIRE_VOLUMETRIE_PREP_CHIMIQUE': return { text: 'Préparation VOL', color: theme.palette.warning.main, bgColor: theme.palette.warning.main };
      case 'LABORATOIRE_VOLUMETRIE_ANALYSE': return { text: 'Analyse VOL', color: theme.palette.primary.main, bgColor: theme.palette.primary.main };
      case 'VALIDATION_SERVICE': return { text: 'Validation', color: theme.palette.secondary.main, bgColor: theme.palette.secondary.main };
      case 'GENERATION_CERTIFICAT': return { text: 'Génération Certificat', color: theme.palette.success.main, bgColor: theme.palette.success.main };
      case 'PRET': return { text: 'Terminé', color: theme.palette.success.main, bgColor: theme.palette.success.main };
      case 'REJETE': return { text: 'Rejeté', color: theme.palette.error.main, bgColor: theme.palette.error.main };
      default: return { text: statut, color: theme.palette.text.secondary, bgColor: theme.palette.grey[500] };
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'URGENTE': return 'error';
      case 'ELEVEE': return 'warning';
      default: return 'default';
    }
  };

  // 🚀 PERFORMANCE: Optimized data loading
  // Statistics are now handled by the optimized hook
  useEffect(() => {
    loadAdminData();
    // No need for interval here - statistics hook handles its own refresh
  }, []); // Only load validation data once
  
  // Update metrics when statistics change
  useEffect(() => {
    if (statistics) {
      const adminMetrics: AdminMetrics = {
        total_samples: statistics.total || 0,
        samples_completed_today: statistics.termines_aujourd_hui || 0,
        active_laboratories: 4,
        total_users: 25,
        system_efficiency: calculateSystemEfficiency(statistics),
        alerts_count: calculateAlertsCount(statistics),
      };
      setMetrics(adminMetrics);
    }
  }, [statistics]);



  // Animate active connections based on sample flow
  useEffect(() => {
    const interval = setInterval(() => {
      const activeNodes = networkNodes.filter(node => node.count > 0);
      const newActiveConnections = new Set<string>();
      
      activeNodes.forEach(node => {
        node.connections.forEach(connId => {
          newActiveConnections.add(`${node.id}-${connId}`);
        });
      });
      
      setActiveConnections(newActiveConnections);
    }, 2000);

    return () => clearInterval(interval);
  }, [statistics]);

  const loadAdminData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // 🚀 PERFORMANCE: Statistics are now loaded by the optimized hook
      // Only load validation data here
      const validationData = await api.get<ValidationData>('/samples/validation-by-source');
      setValidationData(validationData);
      
      // 🚀 PERFORMANCE OPTIMIZATION: Single optimized backend query for all workflow counts
      // Replaces 60,000+ sample fetches with a single ~100ms query
      console.time('⏱️ Workflow counts load time');
      await loadWorkflowCounts();
      console.timeEnd('⏱️ Workflow counts load time');
      
      // Calculate admin-level metrics when statistics are available
      if (statistics) {
        const adminMetrics: AdminMetrics = {
          total_samples: statistics.total || 0,
          samples_completed_today: statistics.termines_aujourd_hui || 0,
          active_laboratories: 4, // SAA, ICP, AU, VOLUMETRIE
          total_users: 25, // This would come from users API
          system_efficiency: calculateSystemEfficiency(statistics),
          alerts_count: calculateAlertsCount(statistics),
        };
        
        setMetrics(adminMetrics);
      }
      
    } catch (error) {
      setError('Erreur lors du chargement des données administrateur');
    } finally {
      setLoading(false);
    }
  };

  const handleNodeClick = async (node: NetworkNodeData) => {
    setSelectedNode(node);
    setLoadingSamples(true);
    setDialogOpen(true);
    setModalPage(1); // Reset to first page when opening new modal
    
    try {
      // Map node info to correct SampleStatus enum values
      const statusFilters: string[] = [];
      
      if (node.id === 'reception') {
        statusFilters.push('RECEPTION');
      } else if (node.id === 'prep_mecanique') {
        // 🔥 BGM FIX: Mechanical prep needs samples from multiple statuses (like Mechanical Prep Dashboard)
        statusFilters.push(
          'PREPARATION_MECANIQUE',
          'LABORATOIRE_AU_PREP_CHIMIQUE',
          'LABORATOIRE_ICP_PREP_CHIMIQUE',
          'LABORATOIRE_AU_ANALYSE',
          'LABORATOIRE_ICP_ANALYSE'
        );
      } else if (node.type === 'validation') {
        // 🔥 BGM FIX: For AU validation, must include BGM samples from ICP workflow!
        if (node.laboratory === 'AU') {
          statusFilters.push('VALIDATION_SERVICE', 'LABORATOIRE_ICP_PREP_CHIMIQUE', 'LABORATOIRE_ICP_ANALYSE');
        } else {
          statusFilters.push('VALIDATION_SERVICE');
        }
      } else if (node.laboratory && node.stage) {
        // 🔥 BGM FIX: For AU prep, we need special handling like AU Prep Dashboard
        if (node.laboratory === 'AU' && node.stage === 'PREP_CHIMIQUE') {
          // For AU prep, we'll fetch ALL samples and filter in frontend (like AU Prep Dashboard)
          // Don't add status filter here - we'll handle it differently
        } else if (node.laboratory === 'ICP' && node.stage === 'PREP_CHIMIQUE') {
          // 🔥 BGM FIX: ICP prep needs samples from multiple statuses (like ICP Prep Dashboard)
          statusFilters.push(
            'LABORATOIRE_ICP_PREP_CHIMIQUE',
            'VALIDATION_SERVICE',
            'LABORATOIRE_AU_PREP_CHIMIQUE',
            'LABORATOIRE_AU_ANALYSE'
          );
        } else {
          statusFilters.push(`LABORATOIRE_${node.laboratory}_${node.stage}`);
        }
      } else if (node.id === 'certificate') {
        statusFilters.push('GENERATION_CERTIFICAT');
      } else if (node.id === 'complete') {
        statusFilters.push('PRET');
      }

      if (statusFilters.length > 0 || (node.laboratory === 'AU' && node.stage === 'PREP_CHIMIQUE')) {
        // 🚀 **CRITICAL FIX**: Fetch ALL samples for the status, not just first 500
        // This ensures modal count matches diagram count exactly
        
        // 🚀 **CRITICAL FIX**: For admin users, bypass laboratory access restrictions
        // The count mismatch was caused by laboratory access filtering in getSamples()
        // while statistics doesn't have this filtering
        
        // 🔄 REVERT: Use regular endpoint that was working before
        // 🔬 BGM FIX: For AU validation nodes, fetch from multiple statuses like supervisor dashboards
        // 🔧 VALIDATION FIX: Get all validation samples, filter in frontend like supervisor dashboards
        
        // 🔥 BGM FIX: For AU prep, fetch ALL samples (like AU Prep Dashboard)
        let queryFilters: any;
        if (node.laboratory === 'AU' && node.stage === 'PREP_CHIMIQUE') {
          // For AU prep, fetch ALL samples without status filter (like AU Prep Dashboard)
          queryFilters = {};
        } else {
          queryFilters = { status: statusFilters as any[] };
        }
        // Note: For AU validation nodes, we already include ICP workflow statuses in statusFilters
        
        // 🚀 **PERFORMANCE FIX**: Get total count first, then use pagination
        const initialResponse = await samplesService.getSamples(1, 1, queryFilters);
        const totalSamples = initialResponse.total;
        
        // Calculate pagination
        const totalPages = Math.ceil(totalSamples / modalPageSize);
        setModalTotalPages(totalPages);
        setStageSamplesTotal(totalSamples);
        
        // 🔧 **PAGINATION OPTIMIZATION**: Always load small batches for better performance
        // 🔥 BGM FIX: Use the same queryFilters we prepared above
        const finalQueryFilters = queryFilters;
        
        // 🚀 PERFORMANCE FIX: Reduced batch size from 2000 to 500 for AU nodes
        // This provides a good balance between showing enough samples and load time
        // For AU nodes we need to fetch more because BGM samples require frontend filtering
        let response;
        if (node.laboratory === 'AU') {
          // For AU validation AND AU prep, fetch more samples to show complete batches
          // Limited to 500 for performance (was 2000 which caused long load times)
          response = await samplesService.getSamples(1, 500, finalQueryFilters);
        } else {
          // For other validations, use pagination
          response = await samplesService.getSamples(modalPage, modalPageSize, finalQueryFilters);
        }
        
        let filteredSamples = response.samples || [];
        
        // 🎯 FRONTEND FILTERING: For validation nodes, filter EXACTLY like supervisor dashboards
        if (node.type === 'validation' && node.laboratory) {
          filteredSamples = filteredSamples.filter((sample: any) => {
            const isBGMSample = sample.metadonnees?.bgm_method_selected === true;
            
            if (node.laboratory === 'SAA') {
              // SAA filtering: only non-BGM samples with SAA analysis data
              return sample.statut === 'VALIDATION_SERVICE' && 
                     sample.metadonnees?.saa_analysis_form && 
                     !isBGMSample;
                     
            } else if (node.laboratory === 'ICP') {
              // ICP filtering: For BGM samples OR non-BGM samples with ICP analysis
              if (isBGMSample) {
                const icpAnalysisComplete = sample.metadonnees?.icp_analysis_completed === true;
                const icpValidationComplete = sample.metadonnees?.icp_validation_completed === true;
                const auValidationComplete = sample.metadonnees?.au_validation_completed === true;

                // Show if ICP analysis is done and either:
                // - ICP validation not done (needs validation)
                // - ICP validated but AU not validated (waiting for AU)
                // 🔥 FIX: BGM samples in ICP workflow (not just VALIDATION_SERVICE)
                return icpAnalysisComplete &&
                       (!icpValidationComplete || (icpValidationComplete && !auValidationComplete));
              } else {
                // Non-BGM ICP samples
                const hasIcpAnalysis = sample.metadonnees?.icp_analysis_form || sample.metadonnees?.icp_analysis_data;
                const icpValidationCompleted = sample.metadonnees?.icp_validation_completed;
                return sample.statut === 'VALIDATION_SERVICE' &&
                       hasIcpAnalysis &&
                       !icpValidationCompleted;
              }
              
            } else if (node.laboratory === 'AU') {
              // AU filtering: For BGM samples OR non-BGM samples with AU analysis
              if (isBGMSample) {
                const auAnalysisComplete = sample.metadonnees?.au_analysis_completed === true;
                const auValidationComplete = sample.metadonnees?.au_validation_completed === true;
                const icpValidationComplete = sample.metadonnees?.icp_validation_completed === true;
                
                // Show if AU analysis is done and either:
                // - AU validation not done (needs validation)
                // - AU validated but ICP not validated (waiting for ICP)
                return auAnalysisComplete && (!auValidationComplete || (auValidationComplete && !icpValidationComplete));
              } else {
                // Non-BGM AU samples
                return sample.statut === 'VALIDATION_SERVICE' &&
                       sample.metadonnees?.au_analysis_form && 
                       !isBGMSample;
              }
              
            } else if (node.laboratory === 'VOLUMETRIE') {
              // VOLUMETRIE filtering: only non-BGM samples
              return sample.statut === 'VALIDATION_SERVICE' &&
                     sample.metadonnees?.volumetrie_analysis_form &&
                     !isBGMSample;
            }
            
            return false;
          });
        }
        
        // 🔥 BGM FIX: For AU prep nodes, apply EXACT same filtering as AU Prep Dashboard
        if (node.laboratory === 'AU' && node.stage === 'PREP_CHIMIQUE') {
          filteredSamples = filteredSamples.filter((sample: any) => {
            // Regular AU samples
            if (sample.statut === 'LABORATOIRE_AU_PREP_CHIMIQUE') {
              return true;
            }
            
            // BGM samples - IGNORE STATUS COMPLETELY, just check metadata (same as AU Prep Dashboard)
            if (sample.metadonnees?.bgm_method_selected === true && 
                sample.metadonnees?.element_lab_routing?.Au === 'LABORATOIRE_AU' &&
                !sample.metadonnees?.au_preparation_completed) {
              return true; // Show ALL BGM samples that need AU prep, regardless of status
            }
            
            return false;
          });
        }
        
        // 🔥 BGM FIX: For ICP prep nodes, apply EXACT same filtering as ICP Prep Dashboard
        if (node.laboratory === 'ICP' && node.stage === 'PREP_CHIMIQUE') {
          filteredSamples = filteredSamples.filter((sample: any) => {
            // Regular ICP samples in prep status
            if (sample.statut === 'LABORATOIRE_ICP_PREP_CHIMIQUE') {
              // For BGM samples, check if both preparations are complete
              if (sample.metadonnees?.bgm_method_selected === true) {
                const icpPrepComplete = sample.metadonnees?.icp_preparation_completed === true;
                const auPrepComplete = sample.metadonnees?.au_preparation_completed === true;
                const bothComplete = icpPrepComplete && auPrepComplete;
                
                // Show if not both complete OR if it was rejected and needs re-prep
                const wasRejected = sample.metadonnees?.validation_data?.action === 'reject';
                const needsReprep = wasRejected || sample.metadonnees?.requires_repreparation === true;
                
                return !bothComplete || needsReprep;
              }
              return true; // Show regular ICP samples
            }
            
            // 🔬 BGM FIX: Show BGM samples from AU workflow that need ICP prep
            if (sample.statut === 'LABORATOIRE_AU_PREP_CHIMIQUE' || 
                sample.statut === 'LABORATOIRE_AU_ANALYSE') {
              const isBGM = sample.metadonnees?.bgm_method_selected === true;
              const icpPrepComplete = sample.metadonnees?.icp_preparation_completed === true;
              
              // Show BGM samples that haven't completed ICP prep yet
              return isBGM && !icpPrepComplete;
            }
            
            // 🚨 BGM FIX: BGM samples in VALIDATION_SERVICE that need ICP prep
            if (sample.statut === 'VALIDATION_SERVICE') {
              const isBGM = sample.metadonnees?.bgm_method_selected === true;
              const icpPrepNotDone = sample.metadonnees?.icp_preparation_completed !== true;
              
              // Show BGM samples that need ICP prep
              return isBGM && icpPrepNotDone;
            }
            
            return false;
          });
        }
        
        // 🔥 BGM FIX: For Mechanical prep node, apply EXACT same filtering as Mechanical Prep Dashboard
        if (node.id === 'prep_mecanique') {
          filteredSamples = filteredSamples.filter((sample: any) => {
            const hasMechanicalMethods = sample.metadonnees?.mechanical_preparation_methods?.length > 0;
            if (!hasMechanicalMethods) return false;
            
            // For BGM samples, check if mechanical prep is completed
            if (sample.metadonnees?.bgm_method_selected === true) {
              // Show BGM samples that haven't completed mechanical prep, regardless of lab status
              return !sample.metadonnees?.bgm_mechanical_prep_completed;
            }
            
            // For regular samples, check status
            return sample.statut === 'PREPARATION_MECANIQUE';
          });
        }

        setStageSamples(filteredSamples);
        // Update total count to reflect frontend filtering for nodes with BGM logic
        const useFilteredCount = 
          (node.type === 'validation' && node.laboratory) || 
          (node.laboratory === 'AU' && node.stage === 'PREP_CHIMIQUE') ||
          (node.laboratory === 'ICP' && node.stage === 'PREP_CHIMIQUE') ||
          node.id === 'prep_mecanique';
        setStageSamplesTotal(useFilteredCount ? filteredSamples.length : response.total);
      } else {
        setStageSamples([]);
        setStageSamplesTotal(0);
      }
    } catch (error) {
      setStageSamples([]);
      setStageSamplesTotal(0);
    } finally {
      setLoadingSamples(false);
    }
  };

  // Handle modal page changes
  const handleModalPageChange = async (newPage: number) => {
    if (newPage === modalPage || !selectedNode) return;
    
    setModalPage(newPage);
    setLoadingSamples(true);
    
    try {
      // Re-run the same query logic with new page
      const statusFilters: string[] = [];
      
      if (selectedNode.id === 'reception') {
        statusFilters.push('RECEPTION');
      } else if (selectedNode.id === 'prep_mecanique') {
        // 🔥 BGM FIX: Mechanical prep needs samples from multiple statuses (like Mechanical Prep Dashboard)
        statusFilters.push(
          'PREPARATION_MECANIQUE',
          'LABORATOIRE_AU_PREP_CHIMIQUE',
          'LABORATOIRE_ICP_PREP_CHIMIQUE',
          'LABORATOIRE_AU_ANALYSE',
          'LABORATOIRE_ICP_ANALYSE'
        );
      } else if (selectedNode.type === 'validation') {
        // 🔥 BGM FIX: For AU validation, must include BGM samples from ICP workflow!
        if (selectedNode.laboratory === 'AU') {
          statusFilters.push('VALIDATION_SERVICE', 'LABORATOIRE_ICP_PREP_CHIMIQUE', 'LABORATOIRE_ICP_ANALYSE');
        } else {
          statusFilters.push('VALIDATION_SERVICE');
        }
      } else if (selectedNode.laboratory && selectedNode.stage) {
        // 🔥 BGM FIX: For AU prep, we need special handling like AU Prep Dashboard
        if (selectedNode.laboratory === 'AU' && selectedNode.stage === 'PREP_CHIMIQUE') {
          // For AU prep, we'll fetch ALL samples and filter in frontend (like AU Prep Dashboard)
          // Don't add status filter here - we'll handle it differently
        } else if (selectedNode.laboratory === 'ICP' && selectedNode.stage === 'PREP_CHIMIQUE') {
          // 🔥 BGM FIX: ICP prep needs samples from multiple statuses (like ICP Prep Dashboard)
          statusFilters.push(
            'LABORATOIRE_ICP_PREP_CHIMIQUE',
            'VALIDATION_SERVICE',
            'LABORATOIRE_AU_PREP_CHIMIQUE',
            'LABORATOIRE_AU_ANALYSE'
          );
        } else {
          statusFilters.push(`LABORATOIRE_${selectedNode.laboratory}_${selectedNode.stage}`);
        }
      } else if (selectedNode.id === 'certificate') {
        statusFilters.push('GENERATION_CERTIFICAT');
      } else if (selectedNode.id === 'complete') {
        statusFilters.push('PRET');
      }

      if (statusFilters.length > 0 || (selectedNode.laboratory === 'AU' && selectedNode.stage === 'PREP_CHIMIQUE')) {
        // 🔥 BGM FIX: For AU prep, fetch ALL samples (like AU Prep Dashboard)
        let queryFilters: any;
        if (selectedNode.laboratory === 'AU' && selectedNode.stage === 'PREP_CHIMIQUE') {
          // For AU prep, fetch ALL samples without status filter (like AU Prep Dashboard)
          queryFilters = {};
        } else {
          queryFilters = { status: statusFilters as any[] };
        }
        
        // 🚀 PERFORMANCE FIX: Reduced batch size from 2000 to 500 for AU nodes
        let response;
        if (selectedNode.laboratory === 'AU') {
          // For AU validation AND AU prep, fetch more samples to show complete batches
          // Limited to 500 for performance (was 2000 which caused long load times)
          response = await samplesService.getSamples(1, 500, queryFilters);
        } else {
          // For other validations, use pagination
          response = await samplesService.getSamples(newPage, modalPageSize, queryFilters);
        }
        let filteredSamples = response.samples || [];
        
        // Apply the same filtering logic for validation nodes - EXACT MATCH with supervisor dashboards
        if (selectedNode.type === 'validation' && selectedNode.laboratory) {
          filteredSamples = filteredSamples.filter((sample: any) => {
            const isBGMSample = sample.metadonnees?.bgm_method_selected === true;
            
            if (selectedNode.laboratory === 'SAA') {
              // SAA filtering: only non-BGM samples with SAA analysis data
              return sample.statut === 'VALIDATION_SERVICE' && 
                     sample.metadonnees?.saa_analysis_form && 
                     !isBGMSample;
                     
            } else if (selectedNode.laboratory === 'ICP') {
              // ICP filtering: For BGM samples OR non-BGM samples with ICP analysis
              if (isBGMSample) {
                const icpAnalysisComplete = sample.metadonnees?.icp_analysis_completed === true;
                const icpValidationComplete = sample.metadonnees?.icp_validation_completed === true;
                const auValidationComplete = sample.metadonnees?.au_validation_completed === true;

                // Show if ICP analysis is done and either:
                // - ICP validation not done (needs validation)
                // - ICP validated but AU not validated (waiting for AU)
                // 🔥 FIX: BGM samples in ICP workflow (not just VALIDATION_SERVICE)
                return icpAnalysisComplete &&
                       (!icpValidationComplete || (icpValidationComplete && !auValidationComplete));
              } else {
                // Non-BGM ICP samples
                const hasIcpAnalysis = sample.metadonnees?.icp_analysis_form || sample.metadonnees?.icp_analysis_data;
                const icpValidationCompleted = sample.metadonnees?.icp_validation_completed;
                return sample.statut === 'VALIDATION_SERVICE' &&
                       hasIcpAnalysis &&
                       !icpValidationCompleted;
              }
              
            } else if (selectedNode.laboratory === 'AU') {
              // AU filtering: For BGM samples OR non-BGM samples with AU analysis
              if (isBGMSample) {
                const auAnalysisComplete = sample.metadonnees?.au_analysis_completed === true;
                const auValidationComplete = sample.metadonnees?.au_validation_completed === true;
                const icpValidationComplete = sample.metadonnees?.icp_validation_completed === true;
                
                // Show if AU analysis is done and either:
                // - AU validation not done (needs validation)
                // - AU validated but ICP not validated (waiting for ICP)
                return auAnalysisComplete && (!auValidationComplete || (auValidationComplete && !icpValidationComplete));
              } else {
                // Non-BGM AU samples
                return sample.statut === 'VALIDATION_SERVICE' &&
                       sample.metadonnees?.au_analysis_form && 
                       !isBGMSample;
              }
              
            } else if (selectedNode.laboratory === 'VOLUMETRIE') {
              // VOLUMETRIE filtering: only non-BGM samples
              return sample.statut === 'VALIDATION_SERVICE' &&
                     sample.metadonnees?.volumetrie_analysis_form &&
                     !isBGMSample;
            }
            
            return false;
          });
        }
        
        // 🔥 BGM FIX: For AU prep nodes, apply EXACT same filtering as AU Prep Dashboard
        if (selectedNode.laboratory === 'AU' && selectedNode.stage === 'PREP_CHIMIQUE') {
          filteredSamples = filteredSamples.filter((sample: any) => {
            // Regular AU samples
            if (sample.statut === 'LABORATOIRE_AU_PREP_CHIMIQUE') {
              return true;
            }
            
            // BGM samples - IGNORE STATUS COMPLETELY, just check metadata (same as AU Prep Dashboard)
            if (sample.metadonnees?.bgm_method_selected === true && 
                sample.metadonnees?.element_lab_routing?.Au === 'LABORATOIRE_AU' &&
                !sample.metadonnees?.au_preparation_completed) {
              return true; // Show ALL BGM samples that need AU prep, regardless of status
            }
            
            return false;
          });
        }
        
        // 🔥 BGM FIX: For ICP prep nodes, apply EXACT same filtering as ICP Prep Dashboard
        if (selectedNode.laboratory === 'ICP' && selectedNode.stage === 'PREP_CHIMIQUE') {
          filteredSamples = filteredSamples.filter((sample: any) => {
            // Regular ICP samples in prep status
            if (sample.statut === 'LABORATOIRE_ICP_PREP_CHIMIQUE') {
              // For BGM samples, check if both preparations are complete
              if (sample.metadonnees?.bgm_method_selected === true) {
                const icpPrepComplete = sample.metadonnees?.icp_preparation_completed === true;
                const auPrepComplete = sample.metadonnees?.au_preparation_completed === true;
                const bothComplete = icpPrepComplete && auPrepComplete;
                
                // Show if not both complete OR if it was rejected and needs re-prep
                const wasRejected = sample.metadonnees?.validation_data?.action === 'reject';
                const needsReprep = wasRejected || sample.metadonnees?.requires_repreparation === true;
                
                return !bothComplete || needsReprep;
              }
              return true; // Show regular ICP samples
            }
            
            // 🔬 BGM FIX: Show BGM samples from AU workflow that need ICP prep
            if (sample.statut === 'LABORATOIRE_AU_PREP_CHIMIQUE' || 
                sample.statut === 'LABORATOIRE_AU_ANALYSE') {
              const isBGM = sample.metadonnees?.bgm_method_selected === true;
              const icpPrepComplete = sample.metadonnees?.icp_preparation_completed === true;
              
              // Show BGM samples that haven't completed ICP prep yet
              return isBGM && !icpPrepComplete;
            }
            
            // 🚨 BGM FIX: BGM samples in VALIDATION_SERVICE that need ICP prep
            if (sample.statut === 'VALIDATION_SERVICE') {
              const isBGM = sample.metadonnees?.bgm_method_selected === true;
              const icpPrepNotDone = sample.metadonnees?.icp_preparation_completed !== true;
              
              // Show BGM samples that need ICP prep
              return isBGM && icpPrepNotDone;
            }
            
            return false;
          });
        }
        
        // 🔥 BGM FIX: For Mechanical prep node, apply EXACT same filtering as Mechanical Prep Dashboard
        if (selectedNode.id === 'prep_mecanique') {
          filteredSamples = filteredSamples.filter((sample: any) => {
            const hasMechanicalMethods = sample.metadonnees?.mechanical_preparation_methods?.length > 0;
            if (!hasMechanicalMethods) return false;
            
            // For BGM samples, check if mechanical prep is completed
            if (sample.metadonnees?.bgm_method_selected === true) {
              // Show BGM samples that haven't completed mechanical prep, regardless of lab status
              return !sample.metadonnees?.bgm_mechanical_prep_completed;
            }
            
            // For regular samples, check status
            return sample.statut === 'PREPARATION_MECANIQUE';
          });
        }
        
        setStageSamples(filteredSamples);
      }
    } catch (error) {
      console.error('Error loading modal page:', error);
    } finally {
      setLoadingSamples(false);
    }
  };

  const renderSankeyFlows = () => {
    return networkNodes.map(node => 
      node.connections.map(targetId => {
        const target = networkNodes.find(n => n.id === targetId);
        if (!target) return null;

        const connectionId = `${node.id}-${targetId}`;
        const isActive = activeConnections.has(connectionId);
        const flowThickness = Math.max(0.05, Math.min(node.count / 15, 1));

        // Create curved path for Sankey flow
        const startX = node.x + 50;
        const startY = node.y + 30;
        const endX = target.x + 50;
        const endY = target.y + 30;
        
        const controlX1 = startX + (endX - startX) * 0.3;
        const controlY1 = startY;
        const controlX2 = endX - (endX - startX) * 0.3;
        const controlY2 = endY;

        const pathData = `M ${startX} ${startY} C ${controlX1} ${controlY1}, ${controlX2} ${controlY2}, ${endX} ${endY}`;

        return (
          <SankeyFlow key={connectionId} active={isActive} thickness={flowThickness}>
            <defs>
              <linearGradient id={`gradient-${connectionId}`} x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="0%" stopColor={alpha(theme.palette.primary.main, 0.8)} />
                <stop offset="30%" stopColor={alpha(theme.palette.secondary.main, 0.9)} />
                <stop offset="70%" stopColor={alpha(theme.palette.success.main, 0.8)} />
                <stop offset="100%" stopColor={alpha(theme.palette.info.main, 0.6)} />
              </linearGradient>
              {isActive && (
                <filter id={`glow-${connectionId}`}>
                  <feGaussianBlur stdDeviation="3" result="coloredBlur"/>
                  <feMerge> 
                    <feMergeNode in="coloredBlur"/>
                    <feMergeNode in="SourceGraphic"/>
                  </feMerge>
                </filter>
              )}
            </defs>
            <path
              className="flow-path"
              d={pathData}
              stroke={`url(#gradient-${connectionId})`}
              filter={isActive ? `url(#glow-${connectionId})` : 'none'}
              strokeDasharray={isActive ? '10 5' : 'none'}
            />
          </SankeyFlow>
        );
      })
    );
  };

  const calculateSystemEfficiency = (stats: SampleStatistics): number => {
    const total = stats.total || 1;
    const completed = stats.par_statut?.PRET || 0;
    return Math.round((completed / total) * 100);
  };

  const calculateAlertsCount = (stats: SampleStatistics): number => {
    let alerts = 0;
    
    // Check for bottlenecks
    if ((stats.par_statut?.PREPARATION_MECANIQUE || 0) > 5) alerts++;
    if ((stats.par_statut?.VALIDATION_SERVICE || 0) > 10) alerts++;
    
    // Check for laboratory backlogs
    Object.entries(stats.par_statut || {}).forEach(([status, count]) => {
      if (status.includes('ANALYSE') && count > 8) alerts++;
    });
    
    return alerts;
  };

  const quickActions: QuickAction[] = [
    {
      title: 'Analytics Avancées',
      description: 'Voir les performances et métriques détaillées',
      icon: <AnalyticsIcon />,
      action: () => navigate('/analytics'),
      color: theme.palette.primary.main,
    },
    {
      title: 'Gestion Utilisateurs',
      description: 'Gérer les comptes et permissions',
      icon: <People />,
      action: () => navigate('/users'),
      color: theme.palette.secondary.main,
    },
    {
      title: 'Vue Laboratoires',
      description: 'Supervision générale des laboratoires',
      icon: <Science />,
      action: () => navigate('/laboratory'),
      color: theme.palette.success.main,
    },
    {
      title: 'Gestion Clients',
      description: 'Clients et projets',
      icon: <Assignment />,
      action: () => navigate('/clients'),
      color: theme.palette.warning.main,
    },
    {
      title: 'Certificats',
      description: 'Statut et génération des certificats',
      icon: <MenuBook />,
      action: () => navigate('/results/certificates'),
      color: theme.palette.info.main,
    },
    {
      title: 'Système & Sécurité',
      description: 'Paramètres système et sécurité',
      icon: <Security />,
      action: () => {}, // Placeholder for system settings
      color: theme.palette.error.main,
    },
  ];

  // Calculate real laboratory efficiency based on actual data
  const calculateLaboratoryEfficiency = (prepSamples: number, analyseSamples: number, completedSamples: number, totalSystemSamples: number) => {
    // If no samples, efficiency is 100% (no backlog)
    if (prepSamples + analyseSamples === 0) return 100;
    
    // Calculate efficiency based on throughput and queue congestion
    const totalLabSamples = prepSamples + analyseSamples;
    const queueFactor = Math.max(0, (10 - totalLabSamples) / 10); // Penalty for high queue
    const throughputFactor = totalSystemSamples > 0 ? completedSamples / totalSystemSamples : 0;
    
    // Combined efficiency: 70% queue management + 30% throughput
    const efficiency = (queueFactor * 70) + (throughputFactor * 30);
    return Math.min(100, Math.max(0, Math.round(efficiency)));
  };

  const getEfficiencyStatus = (efficiency: number) => {
    if (efficiency >= 90) return { status: 'Excellent', color: 'success' };
    if (efficiency >= 80) return { status: 'Bon', color: 'info' };
    if (efficiency >= 70) return { status: 'Moyen', color: 'warning' };
    return { status: 'Attention', color: 'error' };
  };

  const laboratoryStatus = [
    {
      name: 'SAA (Absorption Atomique)',
      samples: (statistics?.par_statut?.LABORATOIRE_SAA_PREP_CHIMIQUE || 0) + 
               (statistics?.par_statut?.LABORATOIRE_SAA_ANALYSE || 0) +
               getValidationSamplesForLab('SAA'),
      efficiency: calculateLaboratoryEfficiency(
        statistics?.par_statut?.LABORATOIRE_SAA_PREP_CHIMIQUE || 0,
        statistics?.par_statut?.LABORATOIRE_SAA_ANALYSE || 0,
        statistics?.par_statut?.PRET || 0,
        getTotalSamples()
      ),
      ...getEfficiencyStatus(calculateLaboratoryEfficiency(
        statistics?.par_statut?.LABORATOIRE_SAA_PREP_CHIMIQUE || 0,
        statistics?.par_statut?.LABORATOIRE_SAA_ANALYSE || 0,
        statistics?.par_statut?.PRET || 0,
        getTotalSamples()
      )),
    },
    {
      name: 'ICP (Spectroscopie)',
      samples: (statistics?.par_statut?.LABORATOIRE_ICP_PREP_CHIMIQUE || 0) + 
               (statistics?.par_statut?.LABORATOIRE_ICP_ANALYSE || 0) +
               getValidationSamplesForLab('ICP'),
      efficiency: calculateLaboratoryEfficiency(
        statistics?.par_statut?.LABORATOIRE_ICP_PREP_CHIMIQUE || 0,
        statistics?.par_statut?.LABORATOIRE_ICP_ANALYSE || 0,
        statistics?.par_statut?.PRET || 0,
        getTotalSamples()
      ),
      ...getEfficiencyStatus(calculateLaboratoryEfficiency(
        statistics?.par_statut?.LABORATOIRE_ICP_PREP_CHIMIQUE || 0,
        statistics?.par_statut?.LABORATOIRE_ICP_ANALYSE || 0,
        statistics?.par_statut?.PRET || 0,
        getTotalSamples()
      )),
    },
    {
      name: 'AU (Analyse Or)',
      samples: (statistics?.par_statut?.LABORATOIRE_AU_PREP_CHIMIQUE || 0) + 
               (statistics?.par_statut?.LABORATOIRE_AU_ANALYSE || 0) +
               getValidationSamplesForLab('AU'),
      efficiency: calculateLaboratoryEfficiency(
        statistics?.par_statut?.LABORATOIRE_AU_PREP_CHIMIQUE || 0,
        statistics?.par_statut?.LABORATOIRE_AU_ANALYSE || 0,
        statistics?.par_statut?.PRET || 0,
        getTotalSamples()
      ),
      ...getEfficiencyStatus(calculateLaboratoryEfficiency(
        statistics?.par_statut?.LABORATOIRE_AU_PREP_CHIMIQUE || 0,
        statistics?.par_statut?.LABORATOIRE_AU_ANALYSE || 0,
        statistics?.par_statut?.PRET || 0,
        getTotalSamples()
      )),
    },
    {
      name: 'Volumétrie',
      samples: (statistics?.par_statut?.LABORATOIRE_VOLUMETRIE_PREP_CHIMIQUE || 0) + 
               (statistics?.par_statut?.LABORATOIRE_VOLUMETRIE_ANALYSE || 0) +
               getValidationSamplesForLab('VOLUMETRIE'),
      efficiency: calculateLaboratoryEfficiency(
        statistics?.par_statut?.LABORATOIRE_VOLUMETRIE_PREP_CHIMIQUE || 0,
        statistics?.par_statut?.LABORATOIRE_VOLUMETRIE_ANALYSE || 0,
        statistics?.par_statut?.PRET || 0,
        getTotalSamples()
      ),
      ...getEfficiencyStatus(calculateLaboratoryEfficiency(
        statistics?.par_statut?.LABORATOIRE_VOLUMETRIE_PREP_CHIMIQUE || 0,
        statistics?.par_statut?.LABORATOIRE_VOLUMETRIE_ANALYSE || 0,
        statistics?.par_statut?.PRET || 0,
        getTotalSamples()
      )),
    },
  ];

  const recentAlerts = [
    ...(((statistics?.par_statut?.PREPARATION_MECANIQUE || 0) > 5) ? [{
      type: 'warning',
      title: 'Goulot d\'étranglement - Préparation Mécanique',
      description: `${statistics?.par_statut?.PREPARATION_MECANIQUE} échantillons en attente`,
      time: 'Il y a 15 min',
    }] : []),
    ...(((statistics?.par_statut?.VALIDATION_SERVICE || 0) > 10) ? [{
      type: 'error',
      title: 'Arriéré de validation important',
      description: `${statistics?.par_statut?.VALIDATION_SERVICE} échantillons en validation`,
      time: 'Il y a 32 min',
    }] : []),
  ];

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress size={60} />
        <Typography variant="h6" sx={{ ml: 2 }}>Chargement du tableau de bord...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Dashboard />
          Tableau de Bord Administrateur
        </Typography>
        <Typography variant="subtitle1" color="text.secondary">
          Bienvenue, {user?.firstName} {user?.lastName} - Vue d'ensemble système AFRILAB
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Real-Time Dashboard Header */}
      <Paper sx={{ p: 2, mb: 3, background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)} 0%, ${alpha(theme.palette.secondary.main, 0.05)} 100%)` }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <MonitorHeart color="primary" sx={{ fontSize: 28 }} />
            <Box>
              <Typography variant="h6" component="h2" sx={{ fontWeight: 600 }}>
                Statut du Laboratoire en Temps Réel
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Vue d'ensemble actuelle du flux de travail • Mise à jour automatique toutes les 30 secondes
              </Typography>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Button
              variant="outlined"
              size="small"
              startIcon={<AnalyticsIcon />}
              onClick={() => navigate('/analytics')}
              sx={{ mr: 2 }}
            >
              Analyse Historique
            </Button>
            <Chip 
              label="TEMPS RÉEL" 
              color="success" 
              variant="outlined"
              sx={{ fontWeight: 600, fontSize: '0.75rem' }}
            />
          </Box>
        </Box>
      </Paper>

      {/* Key Metrics */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={2}>
          <Card sx={{ height: '100%' }}>
            <CardContent sx={{ textAlign: 'center' }}>
              <Avatar sx={{ bgcolor: 'primary.main', mx: 'auto', mb: 1 }}>
                <Assignment />
              </Avatar>
              <Typography variant="h4" color="primary.main">
                {metrics?.total_samples || 0}
              </Typography>
              <Typography variant="caption">Total Échantillons</Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={2}>
          <Card sx={{ height: '100%' }}>
            <CardContent sx={{ textAlign: 'center' }}>
              <Avatar sx={{ bgcolor: 'success.main', mx: 'auto', mb: 1 }}>
                <CheckCircle />
              </Avatar>
              <Typography variant="h4" color="success.main">
                {metrics?.samples_completed_today || 0}
              </Typography>
              <Typography variant="caption">Terminés Aujourd'hui</Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={2}>
          <Card sx={{ height: '100%' }}>
            <CardContent sx={{ textAlign: 'center' }}>
              <Avatar sx={{ bgcolor: 'info.main', mx: 'auto', mb: 1 }}>
                <Science />
              </Avatar>
              <Typography variant="h4" color="info.main">
                {metrics?.active_laboratories || 0}
              </Typography>
              <Typography variant="caption">Laboratoires Actifs</Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={2}>
          <Card sx={{ height: '100%' }}>
            <CardContent sx={{ textAlign: 'center' }}>
              <Avatar sx={{ bgcolor: 'secondary.main', mx: 'auto', mb: 1 }}>
                <People />
              </Avatar>
              <Typography variant="h4" color="secondary.main">
                {metrics?.total_users || 0}
              </Typography>
              <Typography variant="caption">Utilisateurs</Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={2}>
          <Card sx={{ height: '100%' }}>
            <CardContent sx={{ textAlign: 'center' }}>
              <Avatar sx={{ bgcolor: 'warning.main', mx: 'auto', mb: 1 }}>
                <Speed />
              </Avatar>
              <Typography variant="h4" color="warning.main">
                {metrics?.system_efficiency || 0}%
              </Typography>
              <Typography variant="caption">Efficacité Système</Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={2}>
          <Card sx={{ height: '100%' }}>
            <CardContent sx={{ textAlign: 'center' }}>
              <Avatar sx={{ bgcolor: metrics?.alerts_count === 0 ? 'success.main' : 'error.main', mx: 'auto', mb: 1 }}>
                <NotificationsActive />
              </Avatar>
              <Typography variant="h4" color={metrics?.alerts_count === 0 ? 'success.main' : 'error.main'}>
                {metrics?.alerts_count || 0}
              </Typography>
              <Typography variant="caption">Alertes</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* THE SPECTACULAR NETWORK DIAGRAM - RESTORED! */}
      <Slide direction="up" in mountOnEnter>
        <Card sx={{ mb: 4, overflow: 'hidden' }}>
          <CardContent sx={{ p: 0 }}>
            <Box sx={{ p: 3, borderBottom: `1px solid ${theme.palette.divider}` }}>
              <Typography variant="h5" fontWeight={700} sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Timeline sx={{ fontSize: 28, color: 'primary.main' }} />
                Diagramme de Flux Sankey - Laboratoires AFRILAB
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Visualisation du flux d'échantillons • L'épaisseur des connexions représente le volume des échantillons
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, mt: 1, flexWrap: 'wrap' }}>
                <Typography variant="caption" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Inventory2 sx={{ fontSize: 12 }} /> Réception  
                </Typography>
                <Typography variant="caption" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Whatshot sx={{ fontSize: 12 }} /> SAA (Flamme)
                </Typography>
                <Typography variant="caption" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <AutoGraph sx={{ fontSize: 12 }} /> ICP (Spectro)
                </Typography>
                <Typography variant="caption" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Diamond sx={{ fontSize: 12 }} /> AU (Or)
                </Typography>
                <Typography variant="caption" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <WaterDrop sx={{ fontSize: 12 }} /> Volumétrie
                </Typography>
                <Typography variant="caption" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <GppGood sx={{ fontSize: 12 }} /> Validation
                </Typography>
              </Box>
            </Box>
            
            <SankeyContainer ref={sankeyRef}>
              {renderSankeyFlows()}
              
              {networkNodes.map((node) => (
                <InteractiveCard
                  key={node.id}
                  active={node.count > 0}
                  nodeType={node.type}
                  laboratory={node.laboratory}
                  sampleCount={node.count}
                  onClick={() => handleNodeClick(node)}
                  sx={{
                    left: node.x,
                    top: node.y,
                  }}
                >
                  {/* Pulse Ring Effect */}
                  <Box 
                    className="pulse-ring"
                    sx={{
                      position: 'absolute',
                      top: '10px',
                      left: '10px',
                      right: '10px',
                      bottom: '10px',
                      borderRadius: '15px',
                      border: '2px solid currentColor',
                      opacity: node.count > 0 ? 0.6 : 0,
                      pointerEvents: 'none',
                    }}
                  />
                  
                  {/* Progress Ring */}
                  <Box sx={{ position: 'absolute', top: '4px', right: '4px' }}>
                    <svg width="16" height="16" style={{ transform: 'rotate(-90deg)' }}>
                      <circle
                        cx="8"
                        cy="8"
                        r="6"
                        fill="none"
                        stroke="rgba(255,255,255,0.3)"
                        strokeWidth="1.5"
                      />
                      <circle
                        className="progress-ring"
                        cx="8"
                        cy="8"
                        r="6"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeDasharray={`${Math.min((node.count / 20) * 38, 38)} 38`}
                        strokeLinecap="round"
                        style={{
                          transition: 'stroke-dasharray 0.3s ease',
                          opacity: 0.8,
                        }}
                      />
                    </svg>
                  </Box>

                  {/* Icon Container */}
                  <Box 
                    className="icon-container"
                    sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'center',
                      transition: 'transform 0.3s ease',
                      mb: 1,
                      mt: 0.5,
                    }}
                  >
                    {node.icon}
                  </Box>
                  
                  {/* Sample Count */}
                  <Box
                    sx={{
                      position: 'relative',
                      zIndex: 10,
                      backgroundColor: alpha(theme.palette.common.white, 0.25),
                      borderRadius: '12px',
                      padding: '4px 10px',
                      minWidth: '40px',
                      textAlign: 'center',
                      mb: 1,
                      border: `2px solid ${alpha(theme.palette.common.white, 0.3)}`,
                      boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
                    }}
                  >
                    <Typography 
                      variant="h4" 
                      fontWeight={900} 
                      sx={{ 
                        fontSize: '1.6rem',
                        color: theme.palette.common.white,
                        textShadow: '0 2px 6px rgba(0,0,0,0.7)',
                        lineHeight: 1,
                        filter: 'brightness(1.3)',
                      }}
                    >
                      {node.count}
                    </Typography>
                  </Box>
                  
                  {/* Node Name */}
                  <Typography 
                    variant="caption" 
                    fontWeight={700} 
                    sx={{ 
                      fontSize: '0.65rem',
                      opacity: 0.95,
                      textAlign: 'center',
                      whiteSpace: 'pre-line',
                      lineHeight: 1.1,
                      textShadow: '0 1px 3px rgba(0,0,0,0.5)',
                      letterSpacing: '0.3px',
                    }}
                  >
                    {node.shortName}
                  </Typography>

                  {/* Activity Indicator */}
                  {node.count > 0 && (
                    <Box
                      sx={{
                        position: 'absolute',
                        top: '6px',
                        left: '6px',
                        width: '8px',
                        height: '8px',
                        borderRadius: '50%',
                        backgroundColor: theme.palette.warning.main,
                        animation: 'pulse 2s infinite',
                        boxShadow: `0 0 8px ${theme.palette.warning.main}`,
                        '@keyframes pulse': {
                          '0%, 100%': { opacity: 1, transform: 'scale(1)' },
                          '50%': { opacity: 0.7, transform: 'scale(1.2)' },
                        },
                      }}
                    />
                  )}
                </InteractiveCard>
              ))}
              
              {/* Flow direction indicators */}
              <Box sx={{ position: 'absolute', top: 20, left: 20 }}>
                <Typography variant="caption" color="text.secondary" sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: 1,
                  backgroundColor: alpha(theme.palette.background.paper, 0.8),
                  padding: 1,
                  borderRadius: 1
                }}>
                  <Timeline sx={{ fontSize: 16 }} />
                  Flux des échantillons • Épaisseur = Volume
                </Typography>
              </Box>
            </SankeyContainer>
          </CardContent>
        </Card>
      </Slide>

      {/* Quick Actions */}
      <Card sx={{ mb: 4 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>Actions Rapides</Typography>
          <Grid container spacing={2}>
            {quickActions.map((action, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <Paper 
                  sx={{ 
                    p: 2, 
                    cursor: 'pointer',
                    transition: 'transform 0.2s',
                    '&:hover': { transform: 'translateY(-4px)' },
                    border: `1px solid ${alpha(action.color, 0.2)}`,
                  }}
                  onClick={action.action}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Avatar sx={{ bgcolor: action.color }}>
                      {action.icon}
                    </Avatar>
                    <Box>
                      <Typography variant="subtitle2">{action.title}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {action.description}
                      </Typography>
                    </Box>
                  </Box>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </CardContent>
      </Card>

      <Grid container spacing={3}>
        {/* Laboratory Status */}
        <Grid item xs={12} md={8}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>État des Laboratoires</Typography>
              <List>
                {laboratoryStatus.map((lab, index) => (
                  <React.Fragment key={index}>
                    <ListItem>
                      <ListItemIcon>
                        <Avatar sx={{ bgcolor: 'primary.main' }}>
                          <Science />
                        </Avatar>
                      </ListItemIcon>
                      <ListItemText
                        primary={lab.name}
                        secondary={
                          <Box>
                            <Typography variant="caption">
                              {lab.samples} échantillons • Efficacité: {lab.efficiency}%
                            </Typography>
                            <LinearProgress
                              variant="determinate"
                              value={lab.efficiency}
                              sx={{ mt: 1 }}
                              color={lab.color as any}
                            />
                          </Box>
                        }
                      />
                      <Chip 
                        label={lab.status} 
                        color={lab.color as any} 
                        size="small" 
                      />
                    </ListItem>
                    {index < laboratoryStatus.length - 1 && <Divider />}
                  </React.Fragment>
                ))}
              </List>
            </CardContent>
          </Card>
        </Grid>

        {/* Alerts & Notifications */}
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <NotificationsActive />
                Alertes Récentes
              </Typography>
              {recentAlerts.length > 0 ? (
                <List>
                  {recentAlerts.map((alert, index) => (
                    <ListItem key={index} divider>
                      <ListItemIcon>
                        {alert.type === 'error' ? (
                          <Error color="error" />
                        ) : (
                          <Warning color="warning" />
                        )}
                      </ListItemIcon>
                      <ListItemText
                        primary={alert.title}
                        secondary={
                          <Box>
                            <Typography variant="caption">{alert.description}</Typography>
                            <Typography variant="caption" display="block" color="text.secondary">
                              {alert.time}
                            </Typography>
                          </Box>
                        }
                      />
                    </ListItem>
                  ))}
                </List>
              ) : (
                <Box sx={{ textAlign: 'center', py: 3 }}>
                  <CheckCircle color="success" sx={{ fontSize: 48, mb: 1 }} />
                  <Typography variant="body2" color="text.secondary">
                    Aucune alerte système
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Tous les systèmes fonctionnent normalement
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Sample Detail Dialog */}
      <Dialog 
        open={dialogOpen} 
        onClose={() => setDialogOpen(false)}
        maxWidth="xl"
        fullWidth
        PaperProps={{
          sx: { 
            borderRadius: 3,
            minHeight: '80vh',
          }
        }}
      >
        <DialogTitle sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between',
          background: selectedNode?.laboratory 
            ? `linear-gradient(135deg, 
                ${selectedNode.type === 'laboratory' && selectedNode.laboratory === 'SAA' ? '#dc2626' : 
                  selectedNode.type === 'laboratory' && selectedNode.laboratory === 'ICP' ? '#059669' : 
                  selectedNode.type === 'laboratory' && selectedNode.laboratory === 'AU' ? '#d97706' : 
                  selectedNode.type === 'laboratory' && selectedNode.laboratory === 'VOLUMETRIE' ? '#7c2d12' : 
                  theme.palette.primary.main}, 
                ${theme.palette.secondary.main})`
            : `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
          color: 'white',
          p: 3,
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
            <Avatar sx={{ 
              width: 64, 
              height: 64, 
              bgcolor: alpha(theme.palette.common.white, 0.2),
              border: `2px solid ${alpha(theme.palette.common.white, 0.3)}`
            }}>
              {selectedNode?.icon}
            </Avatar>
            <Box>
              <Typography variant="h4" fontWeight={800} sx={{ mb: 0.5 }}>
                {selectedNode?.name}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Typography variant="body1" sx={{ opacity: 0.9 }}>
                  {selectedNode?.description}
                </Typography>
                <Chip 
                  label={`${selectedNode?.count} échantillons`}
                  sx={{ 
                    bgcolor: alpha(theme.palette.common.white, 0.2),
                    color: 'white',
                    fontWeight: 600
                  }}
                />
                {selectedNode?.laboratory && (
                  <Chip 
                    label={selectedNode.laboratory}
                    sx={{ 
                      bgcolor: alpha(theme.palette.common.white, 0.15),
                      color: 'white',
                      fontWeight: 600
                    }}
                  />
                )}
              </Box>
            </Box>
          </Box>
          <IconButton 
            onClick={() => setDialogOpen(false)} 
            sx={{ 
              color: 'white',
              '&:hover': {
                bgcolor: alpha(theme.palette.common.white, 0.1)
              }
            }}
          >
            <Close sx={{ fontSize: 28 }} />
          </IconButton>
        </DialogTitle>
        
        <DialogContent sx={{ p: 0 }}>
          {loadingSamples ? (
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'center', 
              alignItems: 'center', 
              py: 8,
              flexDirection: 'column',
              gap: 2 
            }}>
              <CircularProgress size={60} thickness={4} />
              <Typography variant="h6" color="text.secondary">
                Analyse des échantillons en cours...
              </Typography>
            </Box>
          ) : stageSamples.length > 0 ? (
            <Box>
              {/* ENHANCED BATCH/LOT VISUALIZATION */}
              {(() => {
                // Group samples by batch using proper batch logic
                const { batches, individualSamples } = groupSamplesByBatch(stageSamples);
                const hasBatches = Object.keys(batches).length > 0;

                return (
                  <>
                    {/* SUMMARY STATISTICS */}
                    <Box sx={{ 
                      p: 3, 
                      background: `linear-gradient(135deg, 
                        ${alpha(theme.palette.primary.main, 0.05)} 0%, 
                        ${alpha(theme.palette.secondary.main, 0.05)} 100%)`,
                      borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`
                    }}>
                      <Grid container spacing={3}>
                        <Grid item xs={12} md={3}>
                          <Card sx={{ 
                            background: `linear-gradient(135deg, ${theme.palette.success.main}, ${theme.palette.success.dark})`,
                            color: 'white'
                          }}>
                            <CardContent sx={{ p: 2, textAlign: 'center' }}>
                              <Schema sx={{ fontSize: 32, mb: 1 }} />
                              <Typography variant="h6" fontWeight={700}>
                                {stageSamplesTotal || stageSamples.length}
                              </Typography>
                              <Typography variant="caption">
                                À cette étape
                              </Typography>
                              {selectedNode && Math.abs(selectedNode.count - stageSamplesTotal) > selectedNode.count * 0.1 && (
                                <Typography variant="caption" sx={{ display: 'block', color: 'info.main', fontWeight: 600, mt: 0.5 }}>
                                  📊 Diagramme: {selectedNode.count} (inclut BGM)
                                </Typography>
                              )}
                              {stageSamplesTotal > stageSamples.length && (
                                <Typography variant="caption" sx={{ display: 'block', color: 'warning.main', fontWeight: 600 }}>
                                  {stageSamplesTotal > 10000 ? 
                                    `⚡ Performance: ${stageSamples.length}/${stageSamplesTotal}` :
                                    `Affichage: ${stageSamples.length}/${stageSamplesTotal}`
                                  }
                                </Typography>
                              )}
                            </CardContent>
                          </Card>
                        </Grid>

                        <Grid item xs={12} md={3}>
                          <Card sx={{ 
                            background: `linear-gradient(135deg, ${theme.palette.info.main}, ${theme.palette.info.dark})`,
                            color: 'white'
                          }}>
                            <CardContent sx={{ p: 2, textAlign: 'center' }}>
                              <AccountTree sx={{ fontSize: 32, mb: 1 }} />
                              <Typography variant="h6" fontWeight={700}>
                                {Object.keys(batches).length}
                              </Typography>
                              <Typography variant="caption">
                                Lots Détectés
                              </Typography>
                            </CardContent>
                          </Card>
                        </Grid>

                        <Grid item xs={12} md={3}>
                          <Card sx={{ 
                            background: `linear-gradient(135deg, ${theme.palette.warning.main}, ${theme.palette.warning.dark})`,
                            color: 'white'
                          }}>
                            <CardContent sx={{ p: 2, textAlign: 'center' }}>
                              <MonitorHeart sx={{ fontSize: 32, mb: 1 }} />
                              <Typography variant="h6" fontWeight={700}>
                                {individualSamples.length}
                              </Typography>
                              <Typography variant="caption">
                                Échantillons Individuels
                              </Typography>
                            </CardContent>
                          </Card>
                        </Grid>

                        <Grid item xs={12} md={3}>
                          <Card sx={{ 
                            background: `linear-gradient(135deg, ${theme.palette.secondary.main}, ${theme.palette.secondary.dark})`,
                            color: 'white'
                          }}>
                            <CardContent sx={{ p: 2, textAlign: 'center' }}>
                              <NetworkCheck sx={{ fontSize: 32, mb: 1 }} />
                              <Typography variant="h6" fontWeight={700}>
                                {new Set(stageSamples.map(s => s.client?.company_name)).size}
                              </Typography>
                              <Typography variant="caption">
                                Clients Distincts
                              </Typography>
                            </CardContent>
                          </Card>
                        </Grid>
                      </Grid>
                    </Box>

                    {/* BATCH/LOT TABLE VISUALIZATION */}
                    <Box sx={{ 
                      maxHeight: '60vh', 
                      overflow: 'auto',
                      '&::-webkit-scrollbar': {
                        width: '8px',
                      },
                      '&::-webkit-scrollbar-track': {
                        background: alpha(theme.palette.grey[300], 0.3),
                        borderRadius: '10px',
                      },
                      '&::-webkit-scrollbar-thumb': {
                        background: alpha(theme.palette.primary.main, 0.4),
                        borderRadius: '10px',
                      },
                    }}>
                      <TableContainer component={Paper}>
                        <Table>
                          <TableHead>
                            <TableRow sx={{ bgcolor: alpha(theme.palette.primary.main, 0.05) }}>
                              <TableCell sx={{ fontWeight: 700 }}>Échantillon / Lot</TableCell>
                              <TableCell sx={{ fontWeight: 700 }}>Client</TableCell>
                              <TableCell sx={{ fontWeight: 700 }}>Type</TableCell>
                              <TableCell sx={{ fontWeight: 700 }}>Priorité</TableCell>
                              <TableCell sx={{ fontWeight: 700 }}>Durée</TableCell>
                              <TableCell sx={{ fontWeight: 700 }}>Statut</TableCell>
                              <TableCell sx={{ fontWeight: 700 }}>Actions</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {/* Render Batch Samples First */}
                            {Object.entries(batches).map(([batchNumber, batchSamples]) => {
                              const firstSample = batchSamples[0];
                              const isExpanded = expandedBatches.has(batchNumber);
                              const statusDisplay = getStatusDisplay(firstSample.statut);

                              return (
                                <React.Fragment key={batchNumber}>
                                  {/* Batch Header Row - Clickable */}
                                  <TableRow 
                                    sx={{ 
                                      backgroundColor: '#e3f2fd',
                                      cursor: 'pointer',
                                      '&:hover': { backgroundColor: '#bbdefb' },
                                      borderLeft: '4px solid #2196f3'
                                    }}
                                    onClick={() => toggleBatchExpansion(batchNumber)}
                                  >
                                    <TableCell colSpan={7}>
                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                        <IconButton size="small" sx={{ p: 0 }}>
                                          {isExpanded ? <ExpandLess /> : <ExpandMore />}
                                        </IconButton>
                                        <Box sx={{ 
                                          backgroundColor: '#2196f3', 
                                          color: 'white', 
                                          px: 2, 
                                          py: 0.5, 
                                          borderRadius: 1,
                                          fontWeight: 'bold'
                                        }}>
                                          LOT
                                        </Box>
                                        <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#1976d2' }}>
                                          {batchNumber}
                                        </Typography>
                                        <Chip 
                                          label={`${batchSamples.length} échantillons`} 
                                          size="small" 
                                          color="primary" 
                                          variant="outlined"
                                        />
                                        <Chip 
                                          label={statusDisplay.text} 
                                          size="small" 
                                          sx={{ 
                                            backgroundColor: alpha(statusDisplay.bgColor as string, 0.1),
                                            color: statusDisplay.color,
                                            fontWeight: 500
                                          }}
                                          variant="outlined"
                                        />
                                        <Typography variant="body2" color="text.secondary">
                                          Client: {firstSample.client?.company_name || 'N/A'}
                                        </Typography>
                                        <Button 
                                          size="small" 
                                          variant="contained"
                                          color="primary"
                                          startIcon={<Visibility />}
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            handleViewBatch(batchNumber, batchSamples, firstSample.laboratory || '');
                                          }}
                                          sx={{ ml: 'auto' }}
                                        >
                                          Voir le Lot
                                        </Button>
                                      </Box>
                                    </TableCell>
                                  </TableRow>
                                  
                                  {/* Individual Sample Rows - Only show when expanded */}
                                  {isExpanded && batchSamples.map((sample) => {
                                    const sampleStatus = getStatusDisplay(sample.statut);
                                    return (
                                      <TableRow key={sample.id} sx={{ backgroundColor: '#f8f9ff' }}>
                                        <TableCell sx={{ pl: 6 }}>
                                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.main' }}>
                                              <Science sx={{ fontSize: 16 }} />
                                            </Avatar>
                                            <Box>
                                              <Typography variant="body2" fontWeight={600} color="primary.main">
                                                {sample.sample_id}
                                              </Typography>
                                              <Typography variant="caption" color="text.secondary">
                                                ID: {sample.id.slice(0, 8)}...
                                              </Typography>
                                            </Box>
                                          </Box>
                                        </TableCell>
                                        <TableCell>
                                          <Typography variant="body2" fontWeight={500}>
                                            {sample.external_id || 'N/A'}
                                          </Typography>
                                        </TableCell>
                                        <TableCell>
                                          <Chip 
                                            label={sample.type_echantillon} 
                                            size="small" 
                                            sx={{ fontWeight: 500 }}
                                          />
                                        </TableCell>
                                        <TableCell>
                                          <Chip 
                                            label={sample.priorite} 
                                            size="small" 
                                            color={getPriorityColor(sample.priorite)}
                                            sx={{ fontWeight: 500 }}
                                          />
                                        </TableCell>
                                        <TableCell>
                                          <Typography variant="body2" fontWeight={500}>
                                            {calculateDuration(sample.date_reception)}
                                          </Typography>
                                        </TableCell>
                                        <TableCell>
                                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Box sx={{ 
                                              width: 8, 
                                              height: 8, 
                                              borderRadius: '50%', 
                                              bgcolor: sampleStatus.bgColor 
                                            }} />
                                            <Typography variant="caption" sx={{ color: sampleStatus.color }} fontWeight={600}>
                                              {sampleStatus.text}
                                            </Typography>
                                          </Box>
                                        </TableCell>
                                        <TableCell>
                                          <Box sx={{ display: 'flex', gap: 1 }}>
                                            <Button
                                              size="small" 
                                              startIcon={<Visibility />}
                                              onClick={() => navigate(`/samples/${sample.id}`)}
                                              sx={{ minWidth: 'auto' }}
                                            >
                                              Voir
                                            </Button>
                                            <Button
                                              size="small"
                                              startIcon={<QueryStats />}
                                              sx={{ minWidth: 'auto' }}
                                            >
                                              Stats
                                            </Button>
                                          </Box>
                                        </TableCell>
                                      </TableRow>
                                    );
                                  })}
                                </React.Fragment>
                              );
                            })}

                            {/* Render Individual Samples */}
                            {individualSamples.map((sample) => {
                              const statusDisplay = getStatusDisplay(sample.statut);
                              return (
                                <TableRow key={sample.id} hover>
                                  <TableCell>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                      <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.main' }}>
                                        <Science sx={{ fontSize: 16 }} />
                                      </Avatar>
                                      <Box>
                                        <Typography variant="body2" fontWeight={600} color="primary.main">
                                          {sample.sample_id}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                          ID: {sample.id.slice(0, 8)}...
                                        </Typography>
                                      </Box>
                                    </Box>
                                  </TableCell>
                                  <TableCell>
                                    <Box>
                                      <Typography variant="body2" fontWeight={600}>
                                        {sample.client?.company_name || 'N/A'}
                                      </Typography>
                                      <Typography variant="caption" color="text.secondary">
                                        Réf: {sample.external_id || 'Non spécifiée'}
                                      </Typography>
                                    </Box>
                                  </TableCell>
                                  <TableCell>
                                    <Chip 
                                      label={sample.type_echantillon} 
                                      size="small" 
                                      sx={{ fontWeight: 500 }}
                                    />
                                  </TableCell>
                                  <TableCell>
                                    <Chip 
                                      label={sample.priorite} 
                                      size="small" 
                                      color={getPriorityColor(sample.priorite)}
                                      sx={{ fontWeight: 500 }}
                                    />
                                  </TableCell>
                                  <TableCell>
                                    <Typography variant="body2" fontWeight={500}>
                                      {calculateDuration(sample.date_reception)}
                                    </Typography>
                                  </TableCell>
                                  <TableCell>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                      <Box sx={{ 
                                        width: 8, 
                                        height: 8, 
                                        borderRadius: '50%', 
                                        bgcolor: statusDisplay.bgColor 
                                      }} />
                                      <Typography variant="caption" sx={{ color: statusDisplay.color }} fontWeight={600}>
                                        {statusDisplay.text}
                                      </Typography>
                                    </Box>
                                  </TableCell>
                                  <TableCell>
                                    <Box sx={{ display: 'flex', gap: 1 }}>
                                      <Button
                                        size="small" 
                                        startIcon={<Visibility />}
                                        onClick={() => navigate(`/samples/${sample.id}`)}
                                        sx={{ minWidth: 'auto' }}
                                      >
                                        Voir
                                      </Button>
                                      <Button
                                        size="small"
                                        startIcon={<QueryStats />}
                                        sx={{ minWidth: 'auto' }}
                                      >
                                        Stats
                                      </Button>
                                    </Box>
                                  </TableCell>
                                </TableRow>
                              );
                            })}
                          </TableBody>
                        </Table>
                      </TableContainer>
                      
                      {/* Modal Pagination Controls - PERFORMANCE OPTIMIZATION */}
                      {modalTotalPages > 1 && (
                        <Box sx={{ 
                          display: 'flex', 
                          justifyContent: 'space-between', 
                          alignItems: 'center',
                          mt: 2,
                          p: 2,
                          bgcolor: alpha(theme.palette.primary.main, 0.02),
                          borderRadius: 2
                        }}>
                          <Typography variant="body2" color="text.secondary">
                            Page {modalPage} sur {modalTotalPages} • {stageSamplesTotal} échantillons au total
                          </Typography>
                          <Box sx={{ display: 'flex', gap: 1 }}>
                            <Button
                              size="small"
                              variant="outlined"
                              disabled={modalPage === 1 || loadingSamples}
                              onClick={() => handleModalPageChange(modalPage - 1)}
                              sx={{ minWidth: 100 }}
                            >
                              Précédent
                            </Button>
                            <Button
                              size="small"
                              variant="contained"
                              disabled={modalPage >= modalTotalPages || loadingSamples}
                              onClick={() => handleModalPageChange(modalPage + 1)}
                              sx={{ minWidth: 100 }}
                            >
                              Suivant
                            </Button>
                          </Box>
                        </Box>
                      )}
                    </Box>
                  </>
                );
              })()}
            </Box>
          ) : (
            <Box sx={{ 
              textAlign: 'center', 
              py: 8,
              background: `radial-gradient(circle, 
                ${alpha(theme.palette.primary.main, 0.05)} 0%, 
                transparent 70%)`
            }}>
              <Avatar sx={{ 
                width: 80, 
                height: 80, 
                bgcolor: alpha(theme.palette.primary.main, 0.1),
                mx: 'auto',
                mb: 2
              }}>
                <Schema sx={{ fontSize: 40, color: 'primary.main' }} />
              </Avatar>
              <Typography variant="h5" fontWeight={600} color="text.primary" sx={{ mb: 1 }}>
                Station Vide
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                Aucun échantillon présent dans cette station du réseau
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Les échantillons apparaîtront ici lorsqu'ils atteindront cette étape du processus
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Fermer</Button>
        </DialogActions>
      </Dialog>

      {/* Batch View Dialog */}
      <Dialog 
        open={batchViewDialog} 
        onClose={() => setBatchViewDialog(false)}
        maxWidth={false}
        fullWidth
        PaperProps={{ 
          sx: { 
            minHeight: '80vh',
            maxWidth: '95vw',
            backgroundColor: '#f8f9fa'
          } 
        }}
      >
        <DialogTitle sx={{ backgroundColor: '#1976d2', color: 'white' }}>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Box display="flex" alignItems="center" gap={2}>
              <Visibility sx={{ fontSize: '1.8rem' }} />
              <Box>
                <Typography variant="h5" sx={{ fontSize: '1.2rem', fontWeight: 'bold' }}>
                  📦 Vue Détaillée du Lot: {selectedBatchForView?.batchNumber}
                </Typography>
                <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)', fontSize: '0.8rem' }}>
                  {selectedBatchForView?.samples.length} échantillons • Laboratoire: {selectedBatchForView?.laboratory}
                </Typography>
              </Box>
            </Box>
            <IconButton 
              onClick={() => setBatchViewDialog(false)}
              sx={{ color: 'white' }}
            >
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        
        <DialogContent sx={{ p: 3 }}>
          {selectedBatchForView && (
            <Box>
              {/* Batch Summary Cards */}
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} md={3}>
                  <Card>
                    <CardContent>
                      <Typography color="textSecondary" gutterBottom>Nombre d'Échantillons</Typography>
                      <Typography variant="h4" color="primary">
                        {selectedBatchForView.samples.length}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} md={3}>
                  <Card>
                    <CardContent>
                      <Typography color="textSecondary" gutterBottom>Laboratoire</Typography>
                      <Typography variant="h4" color="secondary">
                        {selectedBatchForView.laboratory}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} md={3}>
                  <Card>
                    <CardContent>
                      <Typography color="textSecondary" gutterBottom>Clients</Typography>
                      <Typography variant="h4" color="success">
                        {new Set(selectedBatchForView.samples.map(s => s.client?.company_name)).size}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} md={3}>
                  <Card>
                    <CardContent>
                      <Typography color="textSecondary" gutterBottom>Statuts</Typography>
                      <Typography variant="h4" color="warning">
                        {new Set(selectedBatchForView.samples.map(s => s.statut)).size} différents
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>

              {/* Detailed Batch Samples List */}
              <Typography variant="h6" gutterBottom>
                📋 Détail des Échantillons du Lot
              </Typography>
              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Position</TableCell>
                      <TableCell>ID Échantillon</TableCell>
                      <TableCell>Client</TableCell>
                      <TableCell>Type</TableCell>
                      <TableCell>Statut</TableCell>
                      <TableCell>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {selectedBatchForView.samples.map((sample, index) => {
                      const batchPosition = index + 1;
                      const statusDisplay = getStatusDisplay(sample.statut);
                      return (
                        <TableRow key={sample.id}>
                          <TableCell>
                            <Chip 
                              label={`Sac ${batchPosition}/${selectedBatchForView.samples.length}`}
                              size="small" 
                              color="primary" 
                              variant="outlined"
                            />
                          </TableCell>
                          <TableCell>
                            <Box>
                              <Typography variant="body2" fontWeight={600} color="primary.main">
                                {sample.sample_id}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                Réf: {sample.external_id || 'N/A'}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell>{sample.client?.company_name || 'N/A'}</TableCell>
                          <TableCell>
                            <Chip label={sample.type_echantillon} size="small" />
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Box sx={{ 
                                width: 8, 
                                height: 8, 
                                borderRadius: '50%', 
                                bgcolor: statusDisplay.bgColor 
                              }} />
                              <Typography variant="caption" sx={{ color: statusDisplay.color }}>
                                {statusDisplay.text}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Button
                              size="small"
                              startIcon={<Visibility />}
                              onClick={() => navigate(`/samples/${sample.id}`)}
                            >
                              Voir
                            </Button>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBatchViewDialog(false)}>Fermer</Button>
        </DialogActions>
      </Dialog>

      {/* TV Dashboard Floating Action Button */}
      <Fab
        color="secondary"
        aria-label="TV Dashboard"
        sx={{
          position: 'fixed',
          bottom: 24,
          right: 24,
          background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
          '&:hover': {
            background: 'linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%)',
            transform: 'scale(1.1)',
          },
          transition: 'all 0.3s ease',
          zIndex: 1000,
        }}
        onClick={() => window.open('/tv-dashboard', '_blank')}
      >
        <TvIcon />
      </Fab>
    </Box>
  );
};

export default AdminDashboard; 